import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, LogIn, AlertCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface SignInPromptProps {
  isOpen: boolean
  onClose: () => void
  onSignIn?: () => void
  title?: string
  message?: string
  action?: string
}

const SignInPrompt: React.FC<SignInPromptProps> = ({
  isOpen,
  onClose,
  onSignIn,
  title = "Sign in to save your progress",
  message = "Create an account to save your data and continue where you left off.",
  action = "Sign In"
}) => {
  const navigate = useNavigate()

  const handleSignIn = () => {
    if (onSignIn) {
      onSignIn()
    } else {
      navigate('/auth?mode=login')
    }
    onClose()
  }

  const handleSignUp = () => {
    navigate('/auth?mode=signup')
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background-950/80 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-background-800 rounded-3xl p-8 shadow-2xl border border-background-700"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-background-400 hover:text-background-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-primary-500/10 border border-primary-500/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-primary-500" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-background-100 mb-4">
                {title}
              </h2>
              <p className="text-background-300 leading-relaxed">
                {message}
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSignIn}
                className="w-full luxury-button primary flex items-center justify-center space-x-2"
              >
                <LogIn className="w-5 h-5" />
                <span>{action}</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSignUp}
                className="w-full luxury-button secondary"
              >
                Create Account
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="w-full px-6 py-3 text-background-400 hover:text-background-200 transition-colors"
              >
                Continue as Guest
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SignInPrompt


